import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import useOcapiSite from '../../../../../hooks/useOcapiSite'
import {WISHLIST_HREF} from '../../../../../constants'
import {HeaderFavoriteIcon} from '../../../icons'
import HeaderIconButton from './header-icon-button'
import HeaderIconLink from './header-icon-link'

/**
 * Header wishlist CTA.
 *
 * Links to the wishlist page and renders only when the `wishlistEnabled` site
 * preference is on. Per STOK-17031 the CTA always looks the same: unlike the
 * legacy header it carries no separate "items present" state, so it stays an
 * outline heart with a single label whether or not the list has products.
 *
 * Rendered through `HeaderIconButton` for the round icon-button chrome
 * (36×36, hover, focus-ring); this component only owns the icon and the
 * accessible name.
 *
 * @component
 * @param {Object|Array} [props.css] - Extra Chakra `css` forwarded to the
 *   underlying `HeaderIconButton` (the header's responsive desktop-only slot).
 * @returns {JSX.Element|null} The wishlist CTA, or null when wishlist is disabled.
 */
const WishlistCta = ({css}) => {
    const {formatMessage} = useIntl()
    const {wishlistEnabled} = useOcapiSite().preferences

    if (!wishlistEnabled) {
        return null
    }

    return (
        <HeaderIconButton
            as={HeaderIconLink}
            css={css}
            to={WISHLIST_HREF}
            aria-label={formatMessage({
                id: 'header.button.assistive_msg.wishlist',
                defaultMessage: 'Wishlist'
            })}
        >
            <HeaderFavoriteIcon />
        </HeaderIconButton>
    )
}

WishlistCta.propTypes = {
    css: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

if (process.env.NODE_ENV !== 'production') {
    WishlistCta.displayName = 'WishlistCta'
}

export default WishlistCta
